import React, { FC, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Table, Form, OverlayTrigger } from 'react-bootstrap';

import { WidgetBase } from 'src/libs/ui-kit/widget-base/WidgetBase';
import { CustomIcon } from 'src/libs/ui-kit/custom-icon/CustomIcon';
import { TCustomIcons } from 'src/libs/ui-kit/custom-icon/interfaces';
import { IconButton } from 'src/libs/ui-kit/buttons/IconButton/IconButton';

import { ForbidDeleteCardTooltip, PaymentMethodView } from './components';
import type { IProvider } from './interfaces';
import { PaymentMethodsStore, StoreContext } from './store';

interface IPaymentMethodsWidgetProps {
  userId: string;
  col: number;
  serviceProvider: IProvider;
  widgetName: string;
}

export const PaymentMethodsWidget: FC<IPaymentMethodsWidgetProps> = observer(
  ({ userId, col, serviceProvider, widgetName = 'Способы оплаты' }) => {
    const store = useMemo(() => new PaymentMethodsStore({ userId, serviceProvider }), [serviceProvider, userId]);
    const buttons = useMemo(
      () => [{ title: 'Привязать', clickHandler: store.addTask, disabled: store.isAddingTask }],
      [store.addTask, store.isAddingTask],
    );

    return (
      <StoreContext.Provider value={store}>
        <WidgetBase
          title={widgetName}
          col={col}
          isLoading={store.paymentMethodsLoading || store.cardAttaching}
          buttons={buttons}>
          <div className="px-5">
            <Table>
              <thead>
                <tr>
                  <th className="border-0 text-tiny pt-0">Способ</th>
                  <th className="border-0 text-tiny pt-0">Основной</th>
                </tr>
              </thead>
              <tbody>
                {store.cardsList.map((card) => (
                  <tr key={`card-${card.id}`}>
                    <td onClick={() => store.showCardInfoModal(card.id!)} className="cursor-pointer align-middle">
                      <CustomIcon name={card.paymentSystem as TCustomIcons} />
                      <span className="ml-1">{`${card.cardNumber}, ${card.expirationDate}`}</span>
                    </td>
                    <td onClick={() => store.showCardInfoModal(card.id!)} className="cursor-pointer align-middle">
                      <Form.Check
                        onClick={(e) => e.stopPropagation()}
                        type="radio"
                        aria-label={`radio-${card.id}`}
                        checked={!!card.isRoot}
                        className="mb-0"
                        onChange={() => store.handleChangeRootCard(card)}
                      />
                    </td>
                    <td className="text-right align-middle">
                      {store.cardsList?.length === 1 || card.isRoot ? (
                        <OverlayTrigger
                          delay={{ show: 250, hide: 400 }}
                          placement="right"
                          overlay={<ForbidDeleteCardTooltip isRoot={!!card.isRoot} />}>
                          <span>
                            <IconButton
                              icon="trash"
                              disabled={!!card.isRoot || store.cardsList?.length === 1}
                              clickHandler={() => store.handleRemoveCard(card)}
                              isLoading={store.isCardDeletePending(card.id!)}
                            />
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <IconButton
                          icon="trash"
                          disabled={!!card.isRoot || store.cardsList?.length === 1}
                          clickHandler={() => store.handleRemoveCard(card)}
                          isLoading={store.isCardDeletePending(card.id!)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </WidgetBase>

        <PaymentMethodView />
      </StoreContext.Provider>
    );
  },
);
